import React, { Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";

const lazyRetry = (importFn) => {
  return React.lazy(() =>
    importFn().catch((error) => {
      console.error("Lazy import failed:", error);
      const isChunkError = 
        error.name === 'ChunkLoadError' || 
        (error.message && (
          error.message.includes('Loading chunk') || 
          error.message.includes('loading chunk') || 
          error.message.includes('Failed to fetch dynamically imported module')
        ));

      if (isChunkError) {
        const lastReload = sessionStorage.getItem("last_chunk_reload");
        const now = Date.now();
        if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
          sessionStorage.setItem("last_chunk_reload", now.toString());
          console.warn("Chunk load failed, reloading page to fetch latest version...");
          window.location.reload();
          return new Promise(() => {}); // Keep pending to avoid error UI flash
        }
      }
      return Promise.reject(error);
    })
  );
};

const SearchBlogs = lazyRetry(() => import("../pages/SearchBlogs"));
const User = lazyRetry(() => import("../pages/User"));
const Comments = lazyRetry(() => import("../pages/Comments"));
const DoctorTabsSection = lazyRetry(() => import("./Doctor/TabsSection"));
const CommunityCategories = lazyRetry(() => import("../pages/CommunityCategories"));
const CommunitySection = lazyRetry(() => import("../pages/Community"));
const CommunityPostDetail = lazyRetry(() => import("../pages/CommunityPostDetail"));
const CommunityUnauthorizedTextSections = lazyRetry(() => import("../pages/CommunityUnauthorizedTextSections"));
const CommunityPostApprove = lazyRetry(() => import("../pages/CommunityPostApprove"));
const AllBlacklistUser = lazyRetry(() => import("./Community/BlacklistUser/AllBlacklistUser"));
const CommunityCommentsApprove = lazyRetry(() => import("../pages/CommunityCommentsApprove"));
const CommunityPostApprovalSection = lazyRetry(() => import("../pages/CommunityPostApprovalSection"));
const EditBlogs = lazyRetry(() => import("../pages/EditBlogs"));
const CreateBlog = lazyRetry(() => import("../pages/CreateBlog"));
const Home = lazyRetry(() => import("./Home/Home"));
const Notifications = lazyRetry(() => import("../pages/Notifications"));
const CompleteChatReport = lazyRetry(() => import("./Home/CompleteChatReport"));
const CompleteComunityReport = lazyRetry(() => import("./Home/CompleteComunityReport"));
const ExportCommunityReport = lazyRetry(() => import("../pages/ExportCommunityReport"));
const DoctorFeedbackDashboard = lazyRetry(() => import("../pages/DoctorFeedbackDashboard"));
const CategoryManager = lazyRetry(() => import("../pages/CategoryManager"));
const ActivityLogs = lazyRetry(() => import("../pages/ActivityLogs"));
const BannerManagement = lazyRetry(() => import("../pages/BannerManagement"));
const ReportRecipients = lazyRetry(() => import("../pages/ReportRecipients"));


const RoleProtectedRoute = ({ children, allowedRoles }) => {
    const auth = useAuthUser();
    const userType = auth?.type || "Admin";

    if (!allowedRoles.includes(userType)) {
        return <Navigate to="/" replace />;
    }

    return children;
};

const AdminPanelContainer = () => {
    return (
        <>
            <main className="py-10 lg:pl-72 bg-white">
                <div className="px-4 sm:px-6 lg:px-8 ">
                    <Suspense
                        fallback={
                            <div className="flex items-center justify-center w-full h-[80vh]">
                                <div className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-amber-500" />
                            </div>
                        }
                    >
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/editBlog" element={<RoleProtectedRoute allowedRoles={['Admin', 'SuperAdmin']}><EditBlogs /></RoleProtectedRoute>} />
                            {/* <Route path="/editPendingNews" element={<EditNewsPending />} /> */}
                            <Route path="/blogs" element={<RoleProtectedRoute allowedRoles={['Admin', 'SuperAdmin']}><SearchBlogs /></RoleProtectedRoute>} />
                            <Route path="/doctor/ayubo" element={<DoctorTabsSection />} />
                            <Route path="/comments" element={<Comments />} />
                            <Route path="/community/all" element={<CommunitySection />} />
                            <Route path="/community/:id" element={<CommunityPostDetail />} />
                            <Route path="/community/categories" element={<CommunityCategories />} />
                            <Route path="/community/approve" element={<CommunityPostApprovalSection />} />
                            <Route path="/community/approve/post/:id" element={<CommunityPostApprove />} />
                            <Route
                                path="/community/approve/post/comments/:id"
                                element={<CommunityCommentsApprove />}
                            />
                            <Route
                                path="/community/manage/texts"
                                element={<CommunityUnauthorizedTextSections />}
                            />
                            <Route path="/community/blacklisted" element={<AllBlacklistUser />} />
                            <Route path="/notifications" element={<RoleProtectedRoute allowedRoles={['Admin', 'SuperAdmin']}><Notifications /></RoleProtectedRoute>} />
                            <Route path="/banners" element={<RoleProtectedRoute allowedRoles={['Admin', 'SuperAdmin']}><BannerManagement /></RoleProtectedRoute>} />
                            <Route path="/create" element={<RoleProtectedRoute allowedRoles={['Admin', 'SuperAdmin']}><CreateBlog /></RoleProtectedRoute>} />
                            <Route path="/user" element={<RoleProtectedRoute allowedRoles={['Admin', 'SuperAdmin']}><User /></RoleProtectedRoute>} />
                            <Route path="/report" element={<RoleProtectedRoute allowedRoles={['Admin', 'SuperAdmin']}><CompleteChatReport /></RoleProtectedRoute>} />
                            <Route path="/reportcom" element={<RoleProtectedRoute allowedRoles={['Admin', 'SuperAdmin']}><CompleteComunityReport /></RoleProtectedRoute>} />
                            <Route path="/community/export" element={<ExportCommunityReport />} />
                            <Route path="/doctor/analytics" element={<RoleProtectedRoute allowedRoles={['Admin', 'SuperAdmin']}><DoctorFeedbackDashboard /></RoleProtectedRoute>} />
                            <Route path="/categories" element={<RoleProtectedRoute allowedRoles={['SuperAdmin']}><CategoryManager /></RoleProtectedRoute>} />
                            <Route path="/activity-logs" element={<RoleProtectedRoute allowedRoles={['SuperAdmin']}><ActivityLogs /></RoleProtectedRoute>} />
                            <Route path="/report-recipients" element={<RoleProtectedRoute allowedRoles={['SuperAdmin']}><ReportRecipients /></RoleProtectedRoute>} />

                        </Routes>
                    </Suspense>
                </div>
            </main>
        </>
    );
};

export default AdminPanelContainer;